import React, { useEffect, useState } from 'react';
import { Card, Table, Badge, Form, Row, Col, Button, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { FaBus, FaSearch, FaFilter, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../styles/Flota.css';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const FlotaUnidades = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unidades, setUnidades] = useState([]);
  const [filtros, setFiltros] = useState({
    estados: [],
    bases: []
  });
  
  const [filtrosSeleccionados, setFiltrosSeleccionados] = useState({
    estado: 'todos',
    base: 'todas',
    busqueda: ''
  });

  const navigate = useNavigate();

  useEffect(() => {
    cargarFiltros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    cargarUnidades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosSeleccionados]);

  const getAuthHeaders = () => {
    const authToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : null;

    if (!authToken || !user) {
      throw new Error('No hay sesión activa');
    }

    const userId = user.Legajo || user.CdEmpleado || user.CdUsuario;
    const userRole = user.IdRol || user.tipoUsuario || 1;

    return {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
      'x-user-id': String(userId),
      'x-user-role': String(userRole)
    };
  };

  const cargarFiltros = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/flota/filtros`, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        setFiltros(response.data.data);
      }
    } catch (err) {
      console.error('Error al cargar filtros:', err);
    }
  };

  const cargarUnidades = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filtrosSeleccionados.estado !== 'todos') {
        params.append('estado', filtrosSeleccionados.estado);
      }
      if (filtrosSeleccionados.base !== 'todas') {
        params.append('base', filtrosSeleccionados.base);
      }
      if (filtrosSeleccionados.busqueda.trim()) {
        params.append('busqueda', filtrosSeleccionados.busqueda.trim());
      }

      const response = await axios.get(
        `${API_URL}/api/flota/unidades?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        setUnidades(response.data.data);
      } else {
        setError('No se pudieron cargar las unidades');
      }
    } catch (err) {
      console.error('Error al cargar unidades:', err);
      setError(err.response?.data?.error || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltrosSeleccionados(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const limpiarFiltros = () => {
    setFiltrosSeleccionados({
      estado: 'todos',
      base: 'todas',
      busqueda: ''
    });
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'Sin registro';
    return new Date(fecha).toLocaleDateString('es-AR');
  };

  const verDetalle = (interno) => {
    navigate(`/administrador/flota/unidades/${interno}`);
  };

  return (
    <div className="flota-unidades">
      <div className="page-header mb-4">
        <h2><FaBus className="me-2" />Listado de Unidades</h2>
        <p className="text-muted">Gestión completa de la flota vehicular</p>
      </div>

      {/* Filtros */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <FaFilter className="me-2" />
          <strong>Filtros de Búsqueda</strong>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={filtrosSeleccionados.estado}
                  onChange={(e) => handleFiltroChange('estado', e.target.value)}
                >
                  <option value="todos">Todos los estados</option>
                  {filtros.estados.map((estado) => (
                    <option key={estado.CdEstado} value={estado.CdEstado}>
                      {estado.NmEstado}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Base</Form.Label>
                <Form.Select
                  value={filtrosSeleccionados.base}
                  onChange={(e) => handleFiltroChange('base', e.target.value)}
                >
                  <option value="todas">Todas las bases</option>
                  {filtros.bases.map((base) => (
                    <option key={base.CdBase} value={base.CdBase}>
                      {base.NmBase}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4} className="mb-3">
              <Form.Group>
                <Form.Label>Buscar</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Interno o dominio..."
                    value={filtrosSeleccionados.busqueda}
                    onChange={(e) => handleFiltroChange('busqueda', e.target.value)}
                  />
                </InputGroup>
              </Form.Group>
            </Col>

            <Col md={2} className="mb-3">
              <Form.Label style={{ opacity: 0 }}>-</Form.Label>
              <Button 
                variant="outline-secondary" 
                onClick={limpiarFiltros}
                className="w-100"
              >
                Limpiar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabla de unidades */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Unidades Registradas</strong>
          <Badge bg="primary">{unidades.length} unidades</Badge>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Cargando unidades...</p>
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : unidades.length === 0 ? (
            <Alert variant="info">No se encontraron unidades con los filtros seleccionados</Alert>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Interno</th>
                    <th>Dominio</th>
                    <th>Estado</th>
                    <th>Base</th>
                    <th>Novedad</th>
                    <th>Última Actividad</th>
                    <th className="text-center">Días Activos (30d)</th>
                    <th className="text-end">Últimos Km</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {unidades.map((unidad) => (
                    <tr key={unidad.Interno}>
                      <td><strong>{unidad.Interno}</strong></td>
                      <td>{unidad.Dominio || '-'}</td>
                      <td>
                        <Badge 
                          bg="secondary"
                          style={{ backgroundColor: unidad.ColorEstado || '#6c757d' }}
                        >
                          {unidad.NmEstado}
                        </Badge>
                      </td>
                      <td>{unidad.NmBase || 'Sin base'}</td>
                      <td>
                        {unidad.CdNovedad !== 0 ? (
                          <Badge bg="danger">{unidad.NmNovedad}</Badge>
                        ) : (
                          <Badge bg="success">Sin novedad</Badge>
                        )}
                      </td>
                      <td>{formatFecha(unidad.UltimaActividad)}</td>
                      <td className="text-center">
                        <Badge bg={unidad.DiasActivosUltimos30 > 15 ? 'success' : unidad.DiasActivosUltimos30 > 5 ? 'warning' : 'danger'}>
                          {unidad.DiasActivosUltimos30 || 0}
                        </Badge>
                      </td>
                      <td className="text-end">
                        {unidad.UltimosKms ? Math.round(unidad.UltimosKms) : '-'}
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => verDetalle(unidad.Interno)}
                          title="Ver detalle"
                        >
                          <FaEye />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default FlotaUnidades;
